import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin, Film, Heart, Users, Clock, Award, Star } from 'lucide-react';
import { KENT, SUFFOLK, ESSEX, LONDON, NORFOLK } from '../data/locations';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const LOCATIONS = { kent: KENT, suffolk: SUFFOLK, essex: ESSEX, london: LONDON, norfolk: NORFOLK };

const CONTACT_URL = 'https://www.weddingvideo.co.uk/contact';
const ALEX_PHOTO = 'https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/ifu7svtq_IMG_1066.jpeg';

// Emotional "biggest regret" hooks — borrowed from WhyWVE, county-localised, photo + video balanced
const buildRegretReasons = (county) => ([
  {
    icon: Film,
    title: `Your ${county} wedding deserves a film and photos you'll actually want to revisit.`,
    body: "Not a boring edit or a generic gallery. A cinematic film and a stunning album that make you feel everything all over again — every single time you press play or turn the page.",
  },
  {
    icon: Heart,
    title: 'Shot by people who love weddings, not just cameras.',
    body: `We've been part of over 900 love stories across the South East. We know the moments that matter in a ${county} wedding — in motion and in stills. We know where to be. You'll forget we're even there.`,
  },
  {
    icon: Users,
    title: 'Your guests will talk about it for years.',
    body: "\"I cried watching your video.\" \"These photos are unreal.\" That's the text you'll get. From your mum. Your bridesmaid. Your partner. Maybe even your nan.",
  },
  {
    icon: Clock,
    title: 'This day only happens once. Don\'t leave it to chance.',
    body: `Your flowers will wilt. Your cake will be eaten. But your ${county} wedding film and photographs? Those are the things that last forever. We take that seriously.`,
  },
]);

const LocationLanding = ({ slug }) => {
  const loc = LOCATIONS[slug?.toLowerCase()];

  useEffect(() => {
    if (!loc) return;
    document.title = loc.metaTitle;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', loc.metaDescription);
    setMeta('keywords', `wedding videographer ${loc.county}, wedding photographer ${loc.county}, luxury wedding videographer ${loc.county}, luxury wedding photographer ${loc.county}, cinematic wedding videographer ${loc.county}, cinematic wedding photographer ${loc.county}, wedding filmmaker ${loc.county}, ${loc.county} wedding films, ${loc.county} wedding photography, documentary wedding videographer ${loc.county}, documentary wedding photographer ${loc.county}`);
  }, [loc]);

  if (!loc) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="text-center text-white">
          <h1 className="font-serif text-3xl mb-4">Location not found</h1>
          <Link to="/" className="text-gold underline">Back to home</Link>
        </div>
      </div>
    );
  }

  const regretReasons = buildRegretReasons(loc.county);
  const testimonials = loc.testimonials || [];

  return (
    <div data-testid={`location-${loc.slug}`} style={{ backgroundColor: '#1A1A1A' }}>
      <Navigation />

      {/* JSON-LD Schema for local SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            '@id': `https://book.weddingvideo.co.uk/${loc.slug}`,
            name: `Wedding Video Essex — Wedding Photographer & Videographer ${loc.county}`,
            description: loc.metaDescription,
            url: `https://book.weddingvideo.co.uk/${loc.slug}`,
            telephone: '+447960090498',
            email: 'hello@weddingvideo.co.uk',
            priceRange: '£££',
            areaServed: { '@type': 'AdministrativeArea', name: loc.county },
            address: { '@type': 'PostalAddress', addressRegion: loc.county, addressCountry: 'GB' },
            sameAs: ['https://www.weddingvideo.co.uk'],
          }),
        }}
      />

      {/* HERO */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#1A1A1A]" />
        <div className="relative container mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/10 border border-gold/30 mb-6">
            <MapPin className="w-4 h-4 text-gold" />
            <span className="text-gold text-xs font-semibold uppercase tracking-widest">{loc.heroKicker}</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
            {loc.h1}
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            {loc.heroSub}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={CONTACT_URL}
              data-testid={`${loc.slug}-cta-primary`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.02]"
            >
              Secure My {loc.county} Date <ChevronRight className="w-4 h-4" />
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 hover:text-white hover:border-white/40 font-medium px-8 py-4 rounded-full transition-all"
            >
              See our work
            </Link>
          </div>

          {/* Trust strip */}
          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white/40 text-xs uppercase tracking-widest">
            <span className="flex items-center gap-2"><Award className="w-3.5 h-3.5" /> Award-winning</span>
            <span className="hidden sm:block w-px h-3 bg-white/20" />
            <span className="flex items-center gap-2"><Film className="w-3.5 h-3.5" /> 1000+ couples filmed &amp; photographed</span>
            <span className="hidden sm:block w-px h-3 bg-white/20" />
            <span className="flex items-center gap-2"><Star className="w-3.5 h-3.5" /> Cinematic. Luxury. Documentary.</span>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="px-4 sm:px-6 lg:px-8 py-20">
        <div className="container mx-auto max-w-3xl">
          {loc.introParagraphs.map((p, i) => (
            <p key={i} className="text-white/75 text-lg leading-relaxed mb-6">{p}</p>
          ))}
        </div>
      </section>

      {/* MEET ALEX — county-flavoured */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-black">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
            <div className="flex-shrink-0">
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-gold/30 shadow-2xl">
                <img src={ALEX_PHOTO} alt={`Alex — wedding photographer and videographer covering ${loc.county}`} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="text-center md:text-left">
              <p className="text-gold text-sm font-semibold uppercase tracking-widest mb-3">
                Founder & creative lead
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mb-2">Meet Alex</h2>
              <p className="text-white/50 text-sm mb-4 italic">Head of Memory Making</p>
              <p className="text-white/70 leading-relaxed text-lg mb-4">
                Alex is the founder and creative lead behind every {loc.county} wedding we cover. He's your point of contact from your very first message, hand-picks the right photographer or filmmaker from our small in-house team for your day, and personally oversees every edit before it reaches you. No call centres. No being passed around. Just one studio that genuinely cares about getting your day right.
              </p>
              <p className="text-white/50 leading-relaxed">
                He founded Wedding Video Essex in 2015 because he believes every couple deserves a wedding film and a photo gallery that makes them feel it all again. Over 900 happy couples later — and many of them right here in {loc.county} — he still gets emotional watching the final edits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY — emotional, regret-framed */}
      <section className="px-4 sm:px-6 lg:px-8 py-24" style={{ backgroundColor: '#FAF8F5' }}>
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-14">
            <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-4">
              Why {loc.county} couples choose us
            </p>
            <h2 className="font-serif text-3xl sm:text-5xl text-gray-900 leading-tight">
              The biggest regret of {loc.county} couples<br className="hidden sm:block" /> who skimp on photo & video?
            </h2>
            <p className="text-gray-500 text-lg mt-4">Skimping on photo & video.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {regretReasons.map((r, i) => {
              const Icon = r.icon;
              return (
                <div key={i} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 text-center group">
                  <div className="w-14 h-14 rounded-xl bg-gray-900 text-gold flex items-center justify-center mb-5 mx-auto group-hover:bg-gold group-hover:text-white transition-colors duration-300">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug">{r.title}</h3>
                  <p className="text-gray-500 leading-relaxed text-sm">{r.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR STYLE */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 border-t border-white/5">
        <div className="container mx-auto max-w-3xl text-center">
          <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-3">Cinematic · Luxury · Documentary</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-8">{loc.styleHeading}</h2>
          {loc.styleParagraphs.map((p, i) => (
            <p key={i} className="text-white/70 text-base sm:text-lg leading-relaxed mb-5">{p}</p>
          ))}
        </div>
      </section>

      {/* VENUES */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 border-t border-white/5">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-3">Venues</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">{loc.venuesHeading}</h2>
            <p className="text-white/50 text-sm max-w-2xl mx-auto">{loc.venuesIntro}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {loc.venues.map((v, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white/70 text-sm hover:border-gold/30 hover:text-white transition-all">
                {v}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — 3 cards, county couples */}
      <section className="px-4 sm:px-6 lg:px-8 py-24" style={{ backgroundColor: '#FAF8F5' }}>
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-4">Don't take our word for it</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-gray-900 mb-3">Hear it from real {loc.county} couples.</h2>
            <p className="text-gray-500 text-lg">Unscripted. Straight from the heart.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100">
                <div className="flex gap-0.5 mb-4">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 text-gold fill-gold" />)}
                </div>
                <p className="font-serif text-gray-800 text-lg leading-snug mb-5">"{t.quote}"</p>
                <div className="text-sm">
                  <p className="font-semibold text-gray-900">{t.author}</p>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mt-0.5">{t.venue}, {loc.county}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AREAS COVERED */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 border-t border-white/5">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-10">
            <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-3">Coverage</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">{loc.areasHeading}</h2>
            <p className="text-white/50 text-sm max-w-2xl mx-auto">{loc.areasIntro}</p>
          </div>
          <p className="text-white/60 text-center text-base leading-loose">
            {loc.areas.map((a, i) => (
              <span key={i}>
                <span className="text-white/80 whitespace-nowrap">{a}</span>
                {i < loc.areas.length - 1 && <span className="text-white/20 mx-2">·</span>}
              </span>
            ))}
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-24 border-t border-white/5">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-serif text-3xl sm:text-5xl text-white mb-6 leading-tight">
            Planning a {loc.county} wedding?
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
            We are a luxury wedding photographer and videographer covering {loc.county} and the wider South East. If you want cinematic, documentary-style wedding photography and film made by a real studio — let us know your date.
          </p>
          <a
            href={CONTACT_URL}
            data-testid={`${loc.slug}-cta-final`}
            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold px-10 py-5 rounded-full text-base transition-all duration-300 hover:scale-[1.02]"
          >
            Secure My {loc.county} Date <ChevronRight className="w-5 h-5" />
          </a>
          <p className="text-white/30 text-xs mt-6 uppercase tracking-widest">Zero pressure · Zero obligation</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocationLanding;
